import { useEffect, useRef, useState } from 'react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/atoms';
import { useOmnichannelMediaUrl } from '@/hooks/use-omnichannel-media-url';
import {
  DownloadIcon,
  FileTextIcon,
  PauseIcon,
  PlayIcon,
  RotateCcwIcon,
  Trash2Icon,
} from 'lucide-react';
import type { MessageMediaType } from '@/types';

function formatFileSize(bytes?: number) {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function formatDuration(ms: number) {
  const total = Math.max(0, Math.floor(ms / 1000));
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${s.toString().padStart(2, '0')}`;
}

export interface MessageMediaContentProps {
  mediaType: MessageMediaType;
  mediaUrl?: string;
  fileName?: string;
  mimeType?: string;
  fileSize?: number;
  caption?: string;
  isOutgoing?: boolean;
  uploadProgress?: number;
  onRetry?: () => void;
  onDelete?: () => void;
}

export function MessageMediaContent({
  mediaType,
  mediaUrl,
  fileName,
  mimeType,
  fileSize,
  caption,
  isOutgoing,
  uploadProgress,
  onRetry,
  onDelete,
}: MessageMediaContentProps) {
  const { displayUrl: resolvedMediaUrl, failed: mediaLoadFailed } =
    useOmnichannelMediaUrl(mediaUrl);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [durationMs, setDurationMs] = useState(0);
  const [positionMs, setPositionMs] = useState(0);

  useEffect(() => {
    const audio = audioRef.current;
    return () => {
      audio?.pause();
    };
  }, [resolvedMediaUrl]);

  if (mediaLoadFailed && !onRetry && !onDelete) {
    return (
      <p className="text-sm opacity-80">
        Media unavailable. Open the thread again.
      </p>
    );
  }

  if ((mediaType === 'image' || mediaType === 'sticker') && resolvedMediaUrl) {
    return (
      <div className="space-y-2">
        <a href={resolvedMediaUrl} target="_blank" rel="noopener noreferrer">
          <img
            src={resolvedMediaUrl}
            alt={
              caption || (mediaType === 'sticker' ? 'Sticker' : 'Shared image')
            }
            className={cn(
              'max-h-56 max-w-full rounded-lg',
              mediaType === 'sticker' ? 'object-contain' : 'object-cover'
            )}
            loading="lazy"
          />
        </a>
        {caption && <p className="text-sm">{caption}</p>}
        {uploadProgress != null && uploadProgress < 100 && (
          <div className="h-1.5 w-full overflow-hidden rounded-full bg-black/10">
            <div
              className="h-full bg-primary transition-all"
              style={{ width: `${uploadProgress}%` }}
            />
          </div>
        )}
      </div>
    );
  }

  if (mediaType === 'video' && resolvedMediaUrl) {
    return (
      <div className="space-y-2">
        <video
          src={resolvedMediaUrl}
          controls
          className="max-h-56 max-w-full rounded-lg"
          preload="metadata"
        />
        {caption && <p className="text-sm">{caption}</p>}
        {uploadProgress != null && uploadProgress < 100 && (
          <div className="h-1.5 w-full overflow-hidden rounded-full bg-black/10">
            <div
              className="h-full bg-primary transition-all"
              style={{ width: `${uploadProgress}%` }}
            />
          </div>
        )}
      </div>
    );
  }

  if ((mediaType === 'document' || mediaType === 'file') && resolvedMediaUrl) {
    const isPdf = mimeType?.includes('pdf');

    return (
      <div className="space-y-2">
        <a
          href={resolvedMediaUrl}
          target="_blank"
          rel="noopener noreferrer"
          download={fileName}
          className={cn(
            'flex items-center gap-3 rounded-lg border px-3 py-2.5 transition-colors',
            isOutgoing
              ? 'border-green-600/40 bg-white/80 hover:bg-white'
              : 'border-white/30 bg-white/10 hover:bg-white/20'
          )}
        >
          <FileTextIcon
            className={cn(
              'size-8 shrink-0 opacity-80',
              isPdf && 'text-red-600'
            )}
          />
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-medium">
              {fileName ?? (mediaType === 'file' ? 'File' : 'Document')}
            </p>
            {fileSize != null && (
              <p className="text-xs opacity-70">{formatFileSize(fileSize)}</p>
            )}
          </div>
          <DownloadIcon className="size-4 shrink-0 opacity-70" />
        </a>
        {caption && <p className="text-sm">{caption}</p>}
        {uploadProgress != null && uploadProgress < 100 && (
          <div className="h-1.5 w-full overflow-hidden rounded-full bg-black/10">
            <div
              className="h-full bg-primary transition-all"
              style={{ width: `${uploadProgress}%` }}
            />
          </div>
        )}
      </div>
    );
  }

  if ((mediaType === 'voice' || mediaType === 'audio') && resolvedMediaUrl) {
    const togglePlay = async () => {
      const el = audioRef.current;
      if (!el) return;
      if (isPlaying) {
        el.pause();
        setIsPlaying(false);
        return;
      }
      try {
        await el.play();
        setIsPlaying(true);
      } catch {
        setIsPlaying(false);
      }
    };

    return (
      <div className="space-y-2">
        <div
          className={cn(
            'flex items-center gap-2 rounded-full px-2 py-1.5',
            isOutgoing ? 'bg-white/90' : 'bg-black/10'
          )}
        >
          <button
            type="button"
            onClick={() => void togglePlay()}
            className={cn(
              'flex size-9 shrink-0 items-center justify-center rounded-full',
              isOutgoing ? 'bg-green-600 text-white' : 'bg-white text-green-700'
            )}
            aria-label={isPlaying ? 'Pause voice note' : 'Play voice note'}
          >
            {isPlaying ? (
              <PauseIcon className="size-4" />
            ) : (
              <PlayIcon className="size-4 ml-0.5" />
            )}
          </button>
          <div className="min-w-0 flex-1">
            <div className="h-1.5 overflow-hidden rounded-full bg-black/10">
              <div
                className="h-full bg-green-600 transition-all"
                style={{
                  width:
                    durationMs > 0
                      ? `${(positionMs / durationMs) * 100}%`
                      : '0%',
                }}
              />
            </div>
            <p className="mt-1 text-[10px] tabular-nums opacity-70">
              {formatDuration(positionMs)} /{' '}
              {durationMs > 0 ? formatDuration(durationMs) : '—'}
            </p>
          </div>
          <audio
            ref={audioRef}
            src={resolvedMediaUrl}
            preload="metadata"
            className="hidden"
            onLoadedMetadata={(e) =>
              setDurationMs(e.currentTarget.duration * 1000)
            }
            onTimeUpdate={(e) =>
              setPositionMs(e.currentTarget.currentTime * 1000)
            }
            onEnded={() => {
              setIsPlaying(false);
              setPositionMs(0);
            }}
          />
        </div>
        {caption && <p className="text-sm">{caption}</p>}
        {uploadProgress != null && uploadProgress < 100 && (
          <div className="h-1.5 w-full overflow-hidden rounded-full bg-black/10">
            <div
              className="h-full bg-primary transition-all"
              style={{ width: `${uploadProgress}%` }}
            />
          </div>
        )}
      </div>
    );
  }

  if (onRetry || onDelete) {
    return (
      <div className="flex gap-2">
        {onRetry && (
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="h-7 gap-1 text-xs"
            onClick={onRetry}
          >
            <RotateCcwIcon className="size-3" />
            Retry
          </Button>
        )}
        {onDelete && (
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="h-7 gap-1 text-xs text-destructive"
            onClick={onDelete}
          >
            <Trash2Icon className="size-3" />
            Delete
          </Button>
        )}
      </div>
    );
  }

  return null;
}
